import { useState, useEffect, useRef, useCallback } from 'react';
import { useEditorSaveRegistry } from '../context/EditorSaveContext';

const DEBOUNCE_MS = 2000;
const SAVED_FLASH_MS = 2000;

// Module-level counter: tracks how many hook instances are currently pending.
// Mirrors onto window.__editorHasPendingSaves so EditorPage's beforeunload
// listener can read it without threading per-form status through props.
let pendingCount = 0;
const PENDING = new Set(['dirty', 'saving', 'error', 'invalid']);

// Debounced, serialized per-section autosave. The form owns save();
// this hook owns when to call it and what the user sees about it.
export default function useAutosave({ sectionKey, data, save, validate, enabled = true }) {
    const [status, setStatus] = useState('idle');
    const dataStr = JSON.stringify(data);

    const snapshotRef = useRef(dataStr);
    const dataStrRef = useRef(dataStr);
    dataStrRef.current = dataStr;
    const saveRef = useRef(save);
    saveRef.current = save;
    const validateRef = useRef(validate);
    validateRef.current = validate;

    const inFlightRef = useRef(null);   // Promise of the running save, or null
    const debounceRef = useRef(null);
    const flashRef = useRef(null);
    const unmountedRef = useRef(false);
    const pendingRef = useRef(false);   // tracks this instance's contribution to pendingCount

    const runSave = useCallback(async () => {
        if (inFlightRef.current) return inFlightRef.current;
        if (dataStrRef.current === snapshotRef.current) return { ok: true };
        if (validateRef.current) {
            const v = validateRef.current();
            if (v === false) { setStatus('invalid'); return { ok: false }; }
            if (v === null || v === undefined) { setStatus('idle'); return { ok: true }; }
        }
        const attempted = dataStrRef.current;
        setStatus('saving');
        const p = (async () => {
            try {
                await saveRef.current();
                snapshotRef.current = attempted;
                inFlightRef.current = null;
                if (unmountedRef.current) return { ok: true };
                if (dataStrRef.current !== snapshotRef.current) {
                    return runSave();               // exactly one follow-up with latest data
                }
                setStatus('saved');
                clearTimeout(flashRef.current);
                flashRef.current = setTimeout(
                    () => setStatus(s => (s === 'saved' ? 'idle' : s)), SAVED_FLASH_MS);
                return { ok: true };
            } catch (e) {
                inFlightRef.current = null;
                if (unmountedRef.current) return { ok: false };
                setStatus('error');
                return { ok: false };
            }
        })();
        inFlightRef.current = p;
        return p;
    }, []);

    // Change detection + debounce.
    useEffect(() => {
        if (!enabled) return undefined;
        if (dataStr === snapshotRef.current) return undefined;
        setStatus(s => (s === 'saving' ? s : 'dirty'));
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(runSave, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [dataStr, enabled, runSave]);

    const flush = useCallback(async () => {
        clearTimeout(debounceRef.current);
        if (inFlightRef.current) await inFlightRef.current;
        return runSave();
    }, [runSave]);

    const retry = flush;

    // Register with EditorPage so Finish can flush everything.
    const registry = useEditorSaveRegistry();
    useEffect(() => {
        if (!registry || !sectionKey) return undefined;
        registry.register(sectionKey, flush);
        return () => registry.unregister(sectionKey);
    }, [registry, sectionKey, flush]);

    // Mirror pending status onto window.__editorHasPendingSaves via module counter.
    useEffect(() => {
        const isPending = PENDING.has(status);
        if (isPending && !pendingRef.current) { pendingCount += 1; pendingRef.current = true; }
        if (!isPending && pendingRef.current) { pendingCount -= 1; pendingRef.current = false; }
        window.__editorHasPendingSaves = pendingCount > 0;
        return () => {
            if (pendingRef.current) { pendingCount -= 1; pendingRef.current = false; window.__editorHasPendingSaves = pendingCount > 0; }
        };
    }, [status]);

    useEffect(() => () => { unmountedRef.current = true; clearTimeout(debounceRef.current); clearTimeout(flashRef.current); }, []);

    return { status, flush, retry };
}
